import { CHECKOUT_ITEM_TYPE_BEDBANK, CHECKOUT_ITEM_TYPE_LE_HOTEL } from 'constants/checkout'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { pickAccommodationV2RateFields } from 'lib/checkout/accommodation/cart'

type PropertyRateUpgradeItem = App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem

/**
 * Toggle a property-rate package upgrade by re-shaping the item to the other side — synchronously, from
 * metadata captured at build time (eager dual-session; no network call). Applied → the target rate (books
 * target, charges source); removed → the source rate with `loyaltyAppliedUpgradeType` cleared but
 * `loyaltyPropertyRateUpgrade` kept so it can re-apply. `itemId`/`transactionKey`/occupancy/dates are
 * preserved; cross-service toggles flip `itemType` (`hotel` ↔ `bedbank`). No-op without an upgrade.
 */
function buildToggledPropertyRateItem(item: PropertyRateUpgradeItem): PropertyRateUpgradeItem {
  const upgrade = item.loyaltyPropertyRateUpgrade
  if (!upgrade) return item

  // Currently applied (undefined ⇒ applied) → remove; currently removed → re-apply.
  const nextApplied = upgrade.applied === false
  const ref = nextApplied ? upgrade.target : upgrade.source
  const booking = nextApplied ? upgrade.targetBooking : upgrade.sourceBooking
  const nextUpgrade: App.Checkout.PropertyRateLoyaltyUpgrade = { ...upgrade, applied: nextApplied }

  const common = {
    itemId: item.itemId,
    transactionKey: item.transactionKey,
    occupancy: item.occupancy,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    duration: item.duration,
    // These point at the base/source rate and only make sense while an upgrade is applied.
    loyaltyAppliedUpgradeType: nextApplied ? upgrade.appliedUpgradeType : undefined,
    loyaltyBasePackageId: nextApplied ? upgrade.source.servicePackageId : undefined,
    loyaltyBaseRateId: nextApplied ? upgrade.source.serviceRateId : undefined,
    loyaltyPropertyRateUpgrade: nextUpgrade,
    propertyRateRef: ref,
    ...(ref.bundleId && { isAccomBundle: ref.bundleId, accommBundleIds: [ref.bundleId] }),
    ...(item.bundledItemIds && { bundledItemIds: item.bundledItemIds }),
  }

  if (ref.serviceType === 'bedbank') {
    const bedbankItem: App.Checkout.BedbankHotelItem = {
      ...common,
      offerId: booking!.offerId,
      sessionId: booking?.sessionId,
      itemType: CHECKOUT_ITEM_TYPE_BEDBANK,
      // Bedbank items key off the bedbank *service* roomId — used to match `bedbankOfferRates` at checkout.
      roomId: booking!.roomId!,
      roomRateId: ref.serviceRateId,
      rateGroupId: booking?.rateGroupId,
      bedGroupId: booking?.bedGroupId ?? '',
      isFlightBundle: booking?.isFlightBundle ?? false,
      boardCode: booking?.boardCode,
      refundable: booking?.refundable ?? false,
    }
    return bedbankItem
  }

  const leItem: App.Checkout.InstantBookingLEHotelItem = {
    ...common,
    offerId: booking?.offerId ?? '',
    itemType: CHECKOUT_ITEM_TYPE_LE_HOTEL,
    reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
    packageId: ref.servicePackageId ?? '',
    roomRateId: ref.serviceRateId,
    paymentScheduleInfo: booking?.paymentScheduleInfo,
  }
  return leItem
}

/**
 * Whether an accomV2 item's loyalty package upgrade can be toggled, which needs both sides' rate fields.
 */
export function canToggleAccommodationV2PropertyRateUpgrade(item: App.Checkout.AccommodationV2Item): boolean {
  const upgrade = item.loyaltyPropertyRateUpgrade
  return !!upgrade?.sourceAccommodationV2Rate && !!upgrade.targetAccommodationV2Rate
}

/**
 * Toggle an accomV2 item's loyalty package upgrade by pointing it at the other side's rate.
 *
 * The side being left is kept as the item has it now, session included. The result carries the other
 * side's last session, if it has one; an accomV2 session is bound to one rate (and the upgrade's
 * discount), so the caller decides whether that session is still usable or mints a new one. No-op
 * without both sides' rate fields.
 */
export function buildToggledAccommodationV2Item(item: App.Checkout.AccommodationV2Item): App.Checkout.AccommodationV2Item {
  const upgrade = item.loyaltyPropertyRateUpgrade
  if (!upgrade || !canToggleAccommodationV2PropertyRateUpgrade(item)) return item

  // Currently applied (undefined ⇒ applied) → remove; currently removed → re-apply.
  const nextApplied = upgrade.applied === false
  const rateFields = nextApplied ? upgrade.targetAccommodationV2Rate : upgrade.sourceAccommodationV2Rate
  const leftRateFields = pickAccommodationV2RateFields(item)

  return {
    ...item,
    ...rateFields,
    // Assigned so the left side's session never carries over, as it's bound to the left side's rate.
    sessionId: rateFields?.sessionId,
    // A restored cart brings back the other rate's svc-cart totals. The svc-cart save recomputes them.
    total: undefined,
    memberTotal: undefined,
    nonMemberTotal: undefined,
    loyaltyAppliedUpgradeType: nextApplied ? upgrade.appliedUpgradeType : undefined,
    loyaltyBasePackageId: nextApplied ? upgrade.source.servicePackageId : undefined,
    loyaltyBaseRateId: nextApplied ? upgrade.source.serviceRateId : undefined,
    loyaltyPropertyRateUpgrade: {
      ...upgrade,
      applied: nextApplied,
      ...(nextApplied ? { sourceAccommodationV2Rate: leftRateFields } : { targetAccommodationV2Rate: leftRateFields }),
    },
  }
}

/**
 * Forfeit a rejected loyalty package upgrade by keeping the target rate without its discount.
 *
 * Unlike `buildToggledPropertyRateItem`, this keeps the target package instead of returning to the
 * source package.
 */
export function buildLoyaltyUpgradeForfeitedItem<T extends App.Checkout.BaseAccommodationItem<string>>(item: T): T {
  if (!item.loyaltyPropertyRateUpgrade) return item
  return {
    ...item,
    loyaltyAppliedUpgradeType: undefined,
    loyaltyBasePackageId: undefined,
    loyaltyBaseRateId: undefined,
    loyaltyPropertyRateUpgrade: undefined,
  }
}

export default buildToggledPropertyRateItem
